<!-- Document upload modal overlay -->
<div class="modal-backdrop">
  <div class="modal-panel">

    <!-- Header -->
    <div class="modal-header">
      <h2>{{ 'documentUploadModal.title' | t }}</h2>
      <button
        class="modal-close-btn"
        type="button"
        (click)="onClose()"
        [attr.aria-label]="'modalCommon.closeAria' | t">
        &times;
      </button>
    </div>

    <!-- Body -->
    <div class="modal-body">

      @if (uploadDone()) {
        <!-- Success state -->
        <div class="upload-success">
          <span class="upload-success__badge" aria-hidden="true"></span>
          <p class="success-text">{{ 'documentUploadModal.success' | t }}</p>
          <p class="success-file">{{ 'documentUploadModal.fileCount' | t:{ count: files().length } }}</p>
        </div>
      } @else {

        <p class="modal-intro">
          {{ 'documentUploadModal.intro' | t }}
        </p>

        <ul class="cr-check-list upload-check-list">
          <li>{{ 'documentUploadModal.secure' | t }}</li>
          <li>{{ 'documentUploadModal.formats' | t }}</li>
          <li>{{ 'documentUploadModal.assigned' | t }}</li>
        </ul>

        @if (showDevHelper) {
          <div class="dev-helper-box" role="note">
            <div class="dev-helper-head">{{ 'documentUploadModal.devTitle' | t }}</div>
            <div class="dev-helper-body">{{ 'documentUploadModal.devBody' | t }}</div>
            <button
              type="button"
              class="btn-dev-fill"
              [disabled]="isUploading()"
              (click)="onSimulateUpload()">
              {{ 'modalCommon.insertTestData' | t }}
            </button>
          </div>
        }

        <div class="upload-fields">
          <label>
            <span>{{ 'documentUploadModal.category' | t }}</span>
            <select [value]="category()" (change)="onCategoryChange($any($event.target).value)" [disabled]="isUploading()">
              <option value="proof">{{ 'documentUploadModal.categoryProof' | t }}</option>
              <option value="invoice">{{ 'documentUploadModal.categoryInvoice' | t }}</option>
              <option value="correspondence">{{ 'documentUploadModal.categoryCorrespondence' | t }}</option>
              <option value="other">{{ 'documentUploadModal.categoryOther' | t }}</option>
            </select>
          </label>
          <label>
            <span>{{ 'documentUploadModal.reason' | t }}</span>
            <input
              type="text"
              [value]="reason()"
              (input)="onReasonChange($any($event.target).value)"
              [disabled]="isUploading()"
              maxlength="120">
          </label>
          <label>
            <span>{{ 'documentUploadModal.comment' | t }} *</span>
            <textarea
              id="document-upload-comment"
              rows="2"
              [value]="comment()"
              (input)="onCommentChange($any($event.target).value)"
              [disabled]="isUploading()"
              required
              aria-required="true"
              [attr.aria-invalid]="isDocumentUploadCommentTooLong() ? 'true' : null"
              [attr.aria-describedby]="isDocumentUploadCommentTooLong() ? 'document-upload-comment-counter document-upload-comment-error' : 'document-upload-comment-counter'"></textarea>
            <small id="document-upload-comment-counter">
              {{ documentUploadCommentLength() }} / {{ documentUploadCommentMaxLength }}
            </small>
            @if (isDocumentUploadCommentTooLong()) {
              <small id="document-upload-comment-error" class="upload-field-error" role="alert">
                {{ 'documentUploadModal.commentTooLong' | t }}
              </small>
            }
          </label>
        </div>

        @if (errorMessage()) {
          <p class="upload-error" role="alert">{{ errorMessage() }}</p>
        }

        <!-- Drop zone -->
        <div
          class="drop-zone"
          [class.drag-over]="isDragOver()"
          [class.has-file]="files().length > 0"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave()"
          (drop)="onDrop($event)">

          @if (files().length > 0) {
            <div class="file-list">
              @for (item of files(); track item.name; let i = $index) {
                <div class="file-selected">
                  <span class="file-icon">{{ 'documentUploadModal.file' | t }}</span>
                  <span class="file-name">{{ item.name }}</span>
                  <span class="file-size">{{ item.size / 1024 | number:'1.0-0' }} KB</span>
                  @if (!isUploading()) {
                    <button type="button" class="file-remove" (click)="removeFile(i)" [attr.aria-label]="'documentUploadModal.removeFile' | t">×</button>
                  }
                  @if (isUploading() || item.status === 'done' || item.status === 'error') {
                    <div class="file-progress" role="progressbar" [attr.aria-valuenow]="item.progress" aria-valuemin="0" aria-valuemax="100">
                      <div class="file-progress__fill" [style.width.%]="item.progress"></div>
                    </div>
                  }
                </div>
              }
              @if (!isUploading() && files().length < maxFiles) {
                <label class="file-select-btn file-select-btn--secondary">
                  {{ 'documentUploadModal.chooseMore' | t }}
                  <input
                    type="file"
                    [accept]="acceptedTypes"
                    multiple
                    (change)="onFileSelect($event)"
                    hidden />
                </label>
              }
            </div>
          } @else {
            <div class="drop-prompt">
              <span class="upload-icon">Upload</span>
              <p>{{ 'documentUploadModal.drop' | t }}</p>
              <p class="drop-or">{{ 'documentUploadModal.or' | t }}</p>
              <label class="file-select-btn">
                {{ 'documentUploadModal.chooseFiles' | t }}
                <input
                  type="file"
                  [accept]="acceptedTypes"
                  multiple
                  (change)="onFileSelect($event)"
                  hidden />
              </label>
              <p class="file-select-status" aria-live="polite">{{ selectedFilesStatus() }}</p>
            </div>
          }
        </div>

        @if (isUploading()) {
          <div class="upload-progress">
            <div class="progress-bar">
              <div class="progress-fill"></div>
            </div>
            <p>{{ 'documentUploadModal.uploading' | t }}</p>
          </div>
        }

      }
    </div>

    <!-- Footer -->
    <div class="modal-footer">
      @if (!uploadDone()) {
        <button
          type="button"
          class="btn-cancel"
          (click)="onSkip()">
          {{ 'documentUploadModal.skip' | t }}
        </button>
        <button
          type="button"
          class="btn-submit"
          [disabled]="files().length === 0 || !hasValidComment() || isUploading()"
          (click)="onUpload()">
          @if (isUploading()) {
            {{ 'documentUploadModal.uploadProgress' | t }}
          } @else {
            {{ 'documentUploadModal.upload' | t }}
          }
        </button>
      }
    </div>

  </div>
</div>
